import { ChevronDown, Loader2, AlertCircle } from 'lucide-react'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useJurisdictions } from '@/hooks/useJurisdictions'
import { usePubAuthorities } from '@/hooks/usePubAuthorities'
import { useDocuments } from '@/hooks/useDocuments'
import { getDocumentsByUuids } from '@/api/filterService'

export interface FilterPanelProps {
  selectedJurisdictionUuids: string[]
  setSelectedJurisdictionUuids: React.Dispatch<React.SetStateAction<string[]>>
  selectedPubAuthorityUuids: string[]
  setSelectedPubAuthorityUuids: React.Dispatch<React.SetStateAction<string[]>>
  selectedDocumentLegislationUuids: string[]
  setSelectedDocumentLegislationUuids: React.Dispatch<React.SetStateAction<string[]>>
  jurisdictionError: boolean
  onJurisdictionErrorDismiss: () => void
  onDocumentMapUpdate?: (map: Map<string, string>) => void
  showNotice?: boolean
}

export default function FilterPanel({
  selectedJurisdictionUuids,
  setSelectedJurisdictionUuids,
  selectedPubAuthorityUuids,
  setSelectedPubAuthorityUuids,
  selectedDocumentLegislationUuids,
  setSelectedDocumentLegislationUuids,
  jurisdictionError,
  onJurisdictionErrorDismiss,
  onDocumentMapUpdate,
}: FilterPanelProps) {
  const { t, i18n } = useTranslation()
  const [isJurisdictionDropdownOpen, setIsJurisdictionDropdownOpen] = useState(false)
  const [isAuthorityDropdownOpen, setIsAuthorityDropdownOpen] = useState(false)
  const [isDocDropdownOpen, setIsDocDropdownOpen] = useState(false)
  const [jurisdictionSearch, setJurisdictionSearch] = useState('')
  const [authoritySearch, setAuthoritySearch] = useState('')

  // Refs for click-away detection
  const jurisdictionDropdownRef = useRef<HTMLDivElement>(null)
  const authorityDropdownRef = useRef<HTMLDivElement>(null)
  const docDropdownRef = useRef<HTMLDivElement>(null)

  // Track previous values to detect actual changes
  const prevJurisdictionUuids = useRef<string[]>([])
  const prevPubAuthorityUuids = useRef<string[]>([])
  const isInitialMount = useRef(true)

  // Track if the next change is user-initiated (vs programmatic like loading conversation)
  const isUserChangingJurisdiction = useRef(false)
  const isUserChangingAuthority = useRef(false)

  // Snapshots of selected items at the moment each dropdown opens (for stable ordering)
  const jurisdictionOrderSnapshot = useRef<Set<string>>(new Set())
  const authorityOrderSnapshot = useRef<Set<string>>(new Set())
  const documentOrderSnapshot = useRef<Set<string>>(new Set())


  // Fetch jurisdictions
  const { data: jurisdictions = [], isLoading: isLoadingJurisdictions, error: jurisdictionsError } = useJurisdictions()

  // Fetch pub authorities based on selected jurisdictions
  const {
    data: pubAuthorities = [],
    isLoading: isLoadingAuthorities,
  } = usePubAuthorities({
    jurisdictionUuids: selectedJurisdictionUuids,
    enabled: selectedJurisdictionUuids.length > 0,
  })

  // [] = all authorities (no filter), same convention as documents
  const allAuthoritiesSelected = selectedPubAuthorityUuids.length === 0

  // Fetch documents with pagination + server-side search
  const {
    displayDocuments,
    allFetchedDocuments,
    totalCount: documentTotalCount,
    isLoading: isLoadingDocuments,
    isLoadingMore: isLoadingMoreDocuments,
    isSearchLoading: isSearchingDocuments,
    error: documentsError,
    fetchNextPage: fetchNextDocumentPage,
    hasNextPage: hasNextDocumentPage,
    searchQuery: documentSearch,
    setSearchQuery: setDocumentSearch,
    isSearchActive: isDocumentSearchActive,
    seedDocuments,
  } = useDocuments({
    jurisdictionUuids: selectedJurisdictionUuids,
    pubAuthorityUuids: selectedPubAuthorityUuids.length > 0 ? selectedPubAuthorityUuids : undefined,
    enabled: selectedJurisdictionUuids.length > 0,
  })

  // Format document display title based on language match
  const formatDocTitle = useCallback((doc: { title: string; language_code: string; alt_lang_version_details: { alt_language_code: string | null; alt_language_title: string | null } | null }) => {
    const alt = doc.alt_lang_version_details
    if (!alt?.alt_language_title) return doc.title
    const uiLang = i18n.language.toUpperCase()
    // If doc language matches UI, show doc title first
    if (doc.language_code.toUpperCase() === uiLang) return `${doc.title} / ${alt.alt_language_title}`
    // If alt language matches UI, show alt title first
    if (alt.alt_language_code?.toUpperCase() === uiLang) return `${alt.alt_language_title} / ${doc.title}`
    // Default: doc title first
    return `${doc.title} / ${alt.alt_language_title}`
  }, [i18n.language])

  // Report document title map to parent for enriched feedback filter config
  useEffect(() => {
    if (onDocumentMapUpdate && allFetchedDocuments.length > 0) {
      const map = new Map(allFetchedDocuments.map(d => [d.legislation_uuid, formatDocTitle(d)]))
      onDocumentMapUpdate(map)
    }
  }, [allFetchedDocuments, onDocumentMapUpdate, formatDocTitle])

  // [] = all documents (no filter sent to backend)
  const allDocumentsSelected = selectedDocumentLegislationUuids.length === 0

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (jurisdictionDropdownRef.current && !jurisdictionDropdownRef.current.contains(event.target as Node)) {
        setIsJurisdictionDropdownOpen(false)
        setJurisdictionSearch('')
      }
      if (authorityDropdownRef.current && !authorityDropdownRef.current.contains(event.target as Node)) {
        setIsAuthorityDropdownOpen(false)
        setAuthoritySearch('')
      }
      if (docDropdownRef.current && !docDropdownRef.current.contains(event.target as Node)) {
        setIsDocDropdownOpen(false)
        setDocumentSearch('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Reset authorities and documents when USER changes jurisdictions
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      prevJurisdictionUuids.current = selectedJurisdictionUuids
      return
    }

    const changed =
      JSON.stringify([...selectedJurisdictionUuids].sort()) !==
      JSON.stringify([...prevJurisdictionUuids.current].sort())

    if (changed && isUserChangingJurisdiction.current) {
      setSelectedPubAuthorityUuids([])
      setSelectedDocumentLegislationUuids([])
    }

    isUserChangingJurisdiction.current = false
    prevJurisdictionUuids.current = selectedJurisdictionUuids
  }, [selectedJurisdictionUuids, setSelectedPubAuthorityUuids, setSelectedDocumentLegislationUuids])

  // Reset documents when USER changes authorities
  useEffect(() => {
    const changed =
      JSON.stringify([...selectedPubAuthorityUuids].sort()) !==
      JSON.stringify([...prevPubAuthorityUuids.current].sort())

    if (changed && isUserChangingAuthority.current) {
      setSelectedDocumentLegislationUuids([])
    }

    isUserChangingAuthority.current = false
    prevPubAuthorityUuids.current = selectedPubAuthorityUuids
  }, [selectedPubAuthorityUuids, setSelectedDocumentLegislationUuids])

  // Cleanup: Remove invalid jurisdiction selections. If none remain, reset everything
  // since pub auths and docs are meaningless without a jurisdiction.
  useEffect(() => {
    if (jurisdictions.length === 0 || selectedJurisdictionUuids.length === 0) return
    const validUuids = new Set(jurisdictions.map((j) => j.jurisdiction_uuid))
    const valid = selectedJurisdictionUuids.filter((uuid) => validUuids.has(uuid))
    if (valid.length === selectedJurisdictionUuids.length) return
    setSelectedJurisdictionUuids(valid)
    if (valid.length === 0) {
      setSelectedPubAuthorityUuids([])
      setSelectedDocumentLegislationUuids([])
    }
  }, [jurisdictions, selectedJurisdictionUuids, setSelectedJurisdictionUuids, setSelectedPubAuthorityUuids, setSelectedDocumentLegislationUuids])

  // Cleanup: Remove invalid authority selections. If none remain, reset to [] (= all).
  useEffect(() => {
    if (pubAuthorities.length > 0 && selectedPubAuthorityUuids.length > 0) {
      const validUuids = new Set(pubAuthorities.map((pa) => pa.uuid))
      const validSelections = selectedPubAuthorityUuids.filter((uuid) => validUuids.has(uuid))
      if (validSelections.length === selectedPubAuthorityUuids.length) return
      setSelectedPubAuthorityUuids(validSelections)
    }
  }, [pubAuthorities, selectedPubAuthorityUuids, setSelectedPubAuthorityUuids])

  // Infinite scroll handler for document list
  const handleDocumentScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
      if (hasNextDocumentPage && !isLoadingMoreDocuments) {
        fetchNextDocumentPage()
      }
    }
  }, [hasNextDocumentPage, isLoadingMoreDocuments, fetchNextDocumentPage])

  // When selected UUIDs are set (e.g. conversation restored), fetch their metadata
  // and seed into allFetchedDocuments so they appear at the top of the list immediately.
  // UUIDs not returned are deleted or restricted — remove them from selection.
  useEffect(() => {
    if (selectedDocumentLegislationUuids.length === 0) return
    const missing = selectedDocumentLegislationUuids.filter(
      uuid => !allFetchedDocuments.find(d => d.legislation_uuid === uuid)
    )
    if (missing.length === 0) return
    getDocumentsByUuids(missing).then(found => {
      seedDocuments(found)
      const foundUuids = new Set(found.map(d => d.legislation_uuid))
      const invalid = missing.filter(uuid => !foundUuids.has(uuid))
      if (invalid.length > 0) {
        setSelectedDocumentLegislationUuids(prev => prev.filter(uuid => !invalid.includes(uuid)))
      }
    })
  }, [selectedDocumentLegislationUuids])

  // --- Handlers ---

  const handleJurisdictionToggle = (uuid: string) => {
    isUserChangingJurisdiction.current = true
    setSelectedJurisdictionUuids((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    )
    if (jurisdictionError) {
      onJurisdictionErrorDismiss()
    }
  }

  const handleAuthorityToggle = (uuid: string) => {
    isUserChangingAuthority.current = true
    setSelectedPubAuthorityUuids((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    )
  }

  const handleAllAuthoritiesToggle = () => {
    if (allAuthoritiesSelected) return  // already all — no-op
    isUserChangingAuthority.current = true
    setSelectedPubAuthorityUuids([])
  }

  const handleDocumentToggle = (legislationUuid: string) => {
    setSelectedDocumentLegislationUuids((prev) =>
      prev.includes(legislationUuid) ? prev.filter((r) => r !== legislationUuid) : [...prev, legislationUuid]
    )
  }

  const handleAllDocumentsToggle = () => {
    setSelectedDocumentLegislationUuids([])
  }

  // --- Display labels ---

  const jurisdictionLabel = useMemo(() => {
    if (selectedJurisdictionUuids.length === 0) return t('filters.selectJurisdiction')
    if (selectedJurisdictionUuids.length === 1) {
      const j = jurisdictions.find((j) => j.jurisdiction_uuid === selectedJurisdictionUuids[0])
      return j?.jurisdiction_common_name ?? t('filters.selectJurisdiction')
    }
    return t('filters.jurisdictionsSelected', { count: selectedJurisdictionUuids.length })
  }, [selectedJurisdictionUuids, jurisdictions, t])

  const authorityLabel = pubAuthorities.length === 0
    ? t('filters.noAuthoritiesAvailable')
    : allAuthoritiesSelected
      ? t('filters.allAuthoritiesDefault')
      : selectedPubAuthorityUuids.length === 1
        ? pubAuthorities.find((pa) => pa.uuid === selectedPubAuthorityUuids[0])?.name ?? ''
        : t('filters.authoritiesSelected', { count: selectedPubAuthorityUuids.length })

  const docLabel = allDocumentsSelected
    ? (documentTotalCount > 0 ? t('filters.allDocumentsCount', { count: documentTotalCount }) : t('filters.allDocuments'))
    : selectedDocumentLegislationUuids.length === 1
      ? allFetchedDocuments.find(d => d.legislation_uuid === selectedDocumentLegislationUuids[0])?.title
        ?? t('filters.documentsSelected', { count: 1 })
      : t('filters.documentsSelected', { count: selectedDocumentLegislationUuids.length })

  // --- Filtered lists for search ---

  const filteredJurisdictions = useMemo(() => {
    let list = jurisdictions
    if (jurisdictionSearch.trim()) {
      const lowerQuery = jurisdictionSearch.toLowerCase()
      list = list.filter((j) => j.jurisdiction_common_name.toLowerCase().includes(lowerQuery))
    }
    const snapshot = jurisdictionOrderSnapshot.current
    return [
      ...list.filter((j) => snapshot.has(j.jurisdiction_uuid)),
      ...list.filter((j) => !snapshot.has(j.jurisdiction_uuid)),
    ]
  }, [jurisdictions, jurisdictionSearch, isJurisdictionDropdownOpen])

  const filteredAuthorities = useMemo(() => {
    let list = pubAuthorities
    if (authoritySearch.trim()) {
      const lowerQuery = authoritySearch.toLowerCase()
      list = list.filter((pa) => pa.name.toLowerCase().includes(lowerQuery))
    }
    const snapshot = authorityOrderSnapshot.current
    return [
      ...list.filter((pa) => snapshot.has(pa.uuid)),
      ...list.filter((pa) => !snapshot.has(pa.uuid)),
    ]
  }, [pubAuthorities, authoritySearch, isAuthorityDropdownOpen])

  return (
    <div className="space-y-4">
      {/* Jurisdiction Dropdown */}
      <div>
        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
          {t('filters.jurisdictionSource')}
        </label>

        <div className="relative" ref={jurisdictionDropdownRef}>
          <div className="relative">
            <input
              type="text"
              value={jurisdictionSearch}
              onChange={(e) => {
                setJurisdictionSearch(e.target.value)
                if (!isJurisdictionDropdownOpen) {
                  jurisdictionOrderSnapshot.current = new Set(selectedJurisdictionUuids)
                  setIsJurisdictionDropdownOpen(true)
                }
              }}
              onFocus={() => {
                if (!isJurisdictionDropdownOpen) {
                  jurisdictionOrderSnapshot.current = new Set(selectedJurisdictionUuids)
                  setIsJurisdictionDropdownOpen(true)
                }
              }}
              placeholder={jurisdictionLabel}
              className={`w-full bg-white py-1.5 pl-3 pr-7 rounded-md text-left text-xs text-gray-700 focus:outline-none shadow-sm hover:bg-gray-50 transition-all ${
                selectedJurisdictionUuids.length > 0 ? 'placeholder:font-bold placeholder:text-brand' : 'placeholder:text-gray-700'
              } ${jurisdictionError
                ? 'border-2 border-red-500 ring-4 ring-red-200'
                : 'border border-gray-200 focus:ring-1 focus:ring-brand'
              }`}
            />
            {!jurisdictionSearch && <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />}
          </div>

          {/* Validation Error Message */}
          {jurisdictionError && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-red-50 border-2 border-red-500 rounded-lg p-2 shadow-lg z-50">
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-red-500"></div>
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-red-700 font-medium">
                  {t('filters.selectJurisdictionValidation')}
                </p>
              </div>
            </div>
          )}

          {/* Dropdown Menu */}
          {isJurisdictionDropdownOpen && (
            <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-xl z-50">
              {/* Jurisdiction List */}
              <div className="max-h-60 overflow-y-auto p-1">
                {isLoadingJurisdictions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 text-brand animate-spin" />
                    <span className="ml-2 text-xs text-gray-500">{t('filters.loading')}</span>
                  </div>
                ) : jurisdictionsError ? (
                  <div className="flex items-center px-3 py-2 text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="ml-2 text-xs">{t('filters.failedToLoadJurisdictions')}</span>
                  </div>
                ) : filteredJurisdictions.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-400 text-center">
                    {jurisdictionSearch ? t('filters.noJurisdictionsFound') : t('filters.noJurisdictionsAvailable')}
                  </div>
                ) : (
                  filteredJurisdictions.map((jurisdiction) => (
                    <label
                      key={jurisdiction.jurisdiction_uuid}
                      className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedJurisdictionUuids.includes(jurisdiction.jurisdiction_uuid)}
                        onChange={() => handleJurisdictionToggle(jurisdiction.jurisdiction_uuid)}
                        className="h-3 w-3 text-brand border-gray-300 rounded focus:ring-0"
                      />
                      <span className="ml-2 text-xs text-gray-700">{jurisdiction.jurisdiction_common_name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Reset Link */}
        {selectedJurisdictionUuids.length > 0 && (
          <div className="mt-2 flex justify-end">
            <button
              onClick={() => {
                isUserChangingJurisdiction.current = true
                setSelectedJurisdictionUuids([])
              }}
              className="text-[10px] text-gray-400 hover:text-brand hover:underline"
            >
              {t('filters.reset')}
            </button>
          </div>
        )}
      </div>

      {/* Publishing Authority Dropdown */}
      {selectedJurisdictionUuids.length > 0 && (pubAuthorities.length > 0 || isLoadingAuthorities) && (
        <div>
          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
            {t('filters.publishingAuthority')}
          </label>

          <div className="relative" ref={authorityDropdownRef}>
            <div className="relative">
              <input
                type="text"
                value={authoritySearch}
                onChange={(e) => {
                  setAuthoritySearch(e.target.value)
                  if (!isAuthorityDropdownOpen) {
                    authorityOrderSnapshot.current = new Set(selectedPubAuthorityUuids)
                    setIsAuthorityDropdownOpen(true)
                  }
                }}
                onFocus={() => {
                  if (!isAuthorityDropdownOpen) {
                    authorityOrderSnapshot.current = new Set(selectedPubAuthorityUuids)
                    setIsAuthorityDropdownOpen(true)
                  }
                }}
                placeholder={isLoadingAuthorities ? t('filters.loading') : authorityLabel}
                className={`w-full bg-white py-1.5 pl-3 pr-7 rounded-md text-left text-xs text-gray-700 focus:outline-none shadow-sm hover:bg-gray-50 transition-all border border-gray-200 focus:ring-1 focus:ring-brand ${
                  !allAuthoritiesSelected ? 'placeholder:font-bold placeholder:text-brand' : 'placeholder:text-gray-700'
                }`}
              />
              {!authoritySearch && <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />}
            </div>

            {/* Dropdown Menu */}
            {isAuthorityDropdownOpen && (
              <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-xl z-50">

                {/* Authority List */}
                <div className="max-h-60 overflow-y-auto p-1">
                  {isLoadingAuthorities ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 text-brand animate-spin" />
                      <span className="ml-2 text-xs text-gray-500">{t('filters.loading')}</span>
                    </div>
                  ) : (
                    <>
                      {/* All Authorities Option */}
                      {!authoritySearch && (
                        <label className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer rounded border-b border-gray-100">
                          <input
                            type="checkbox"
                            checked={allAuthoritiesSelected}
                            onChange={handleAllAuthoritiesToggle}
                            className="h-3 w-3 text-brand border-gray-300 rounded focus:ring-0"
                          />
                          <span className="ml-2 text-xs text-gray-700 font-medium">{t('filters.allAuthorities')}</span>
                        </label>
                      )}

                      {filteredAuthorities.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-400 text-center">
                          {t('filters.noAuthoritiesFound')}
                        </div>
                      ) : (
                        filteredAuthorities.map((authority) => (
                          <label
                            key={authority.uuid}
                            className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer rounded"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPubAuthorityUuids.includes(authority.uuid)}
                              onChange={() => handleAuthorityToggle(authority.uuid)}
                              className="h-3 w-3 text-brand border-gray-300 rounded focus:ring-0"
                            />
                            <span className="ml-2 text-xs text-gray-700">{authority.name}</span>
                          </label>
                        ))
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Reset Link */}
          {!allAuthoritiesSelected && (
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => {
                  isUserChangingAuthority.current = true
                  setSelectedPubAuthorityUuids([])
                }}
                className="text-[10px] text-gray-400 hover:text-brand hover:underline"
              >
                {t('filters.resetToAll')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Documents Dropdown */}
      {selectedJurisdictionUuids.length > 0 && (
        <div>
          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
            {t('filters.documents')}
          </label>

          <div className="relative" ref={docDropdownRef}>
            <div className="relative">
              <input
                type="text"
                value={documentSearch}
                onChange={(e) => {
                  setDocumentSearch(e.target.value)
                  if (!isDocDropdownOpen) {
                    documentOrderSnapshot.current = new Set(selectedDocumentLegislationUuids)
                    setIsDocDropdownOpen(true)
                  }
                }}
                onFocus={() => {
                  if (!isDocDropdownOpen) {
                    documentOrderSnapshot.current = new Set(selectedDocumentLegislationUuids)
                    setIsDocDropdownOpen(true)
                  }
                }}
                placeholder={docLabel}
                className={`w-full bg-white py-1.5 pl-3 pr-7 rounded-md text-left text-xs text-gray-700 focus:outline-none shadow-sm hover:bg-gray-50 transition-all border border-gray-200 focus:ring-1 focus:ring-brand ${
                  selectedDocumentLegislationUuids.length > 0 && !allDocumentsSelected ? 'placeholder:font-bold placeholder:text-brand' : 'placeholder:text-gray-700'
                }`}
              />
              {!documentSearch && <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />}
            </div>

            {/* Dropdown Menu */}
            {isDocDropdownOpen && (
              <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-xl z-50">

                {/* Document List (infinite scroll) */}
                <div className="max-h-60 overflow-y-auto p-1" onScroll={handleDocumentScroll}>
                  {isLoadingDocuments ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 text-brand animate-spin" />
                      <span className="ml-2 text-xs text-gray-500">{t('filters.loadingDocuments')}</span>
                    </div>
                  ) : documentsError ? (
                    <div className="flex items-center px-3 py-2 text-red-600">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="ml-2 text-xs">{t('filters.failedToLoadDocuments')}</span>
                    </div>
                  ) : (
                    <>
                      {/* All Documents Option */}
                      {!isDocumentSearchActive && (
                        <label className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer rounded border-b border-gray-100">
                          <input
                            type="checkbox"
                            checked={allDocumentsSelected}
                            onChange={handleAllDocumentsToggle}
                            className="h-3 w-3 text-brand border-gray-300 rounded focus:ring-0"
                          />
                          <span className="ml-2 text-xs text-gray-700 font-medium">
                            {documentTotalCount > 0 ? t('filters.allDocumentsCount', { count: documentTotalCount }) : t('filters.allDocuments')}
                          </span>
                        </label>
                      )}

                      {/* Searching indicator */}
                      {isSearchingDocuments && (
                        <div className="flex items-center justify-center py-2">
                          <Loader2 className="w-3 h-3 text-brand animate-spin" />
                          <span className="ml-2 text-[10px] text-gray-400">{t('filters.searching')}</span>
                        </div>
                      )}

                      {[
                        ...displayDocuments.filter((doc) => documentOrderSnapshot.current.has(doc.legislation_uuid)),
                        ...displayDocuments.filter((doc) => !documentOrderSnapshot.current.has(doc.legislation_uuid)),
                      ].map((doc) => (
                        <label
                          key={doc.legislation_uuid}
                          className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer rounded"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDocumentLegislationUuids.includes(doc.legislation_uuid)}
                            onChange={() => handleDocumentToggle(doc.legislation_uuid)}
                            className="h-3 w-3 text-brand border-gray-300 rounded focus:ring-0"
                          />
                          <span className="ml-2 text-xs text-gray-700">
                            {formatDocTitle(doc)}
                          </span>
                        </label>
                      ))}

                      {/* Loading more indicator */}
                      {isLoadingMoreDocuments && (
                        <div className="flex items-center justify-center py-2">
                          <Loader2 className="w-3 h-3 text-brand animate-spin" />
                          <span className="ml-2 text-[10px] text-gray-400">{t('filters.loadingMore')}</span>
                        </div>
                      )}

                      {!isLoadingDocuments && !isSearchingDocuments && displayDocuments.length === 0 && (
                        <div className="px-3 py-2 text-xs text-gray-400 text-center">
                          {isDocumentSearchActive ? t('filters.noDocumentsFound') : t('filters.noDocumentsAvailable')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Reset Link */}
          {!allDocumentsSelected && (
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => setSelectedDocumentLegislationUuids([])}
                className="text-[10px] text-gray-400 hover:text-brand hover:underline"
              >
                {t('filters.resetToAll')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
